export type FormStatus = 'Draft' | 'Published' | 'Closed';
export type FormAccessType = 'Public' | 'Private';
export type FormPurpose = 'General' | 'Support';
export type FormNavigationAction = 'ContinueToNext' | 'GoToSection' | 'SubmitForm';
export type FormCollectEmail = 'NotCollect' | 'Optional' | 'Required';

export type FormFieldType =
  | 'ShortText'
  | 'Paragraph'
  | 'MultipleChoice'
  | 'Checkboxes'
  | 'Dropdown'
  | 'Date'
  | 'Time'
  | 'FileUpload'
  | 'Rating'
  | 'Image'
  | 'Video';

export interface FormFieldOption {
  id: string;
  label: string;
  order: number;
  goToAction?: FormNavigationAction | null;
  goToTargetSectionId?: string | null;
}

export interface FormField {
  id: string;
  sectionId: string;
  title: string;
  description?: string;
  type: FormFieldType;
  isRequired: boolean;
  order: number;
  validationRegex?: string;
  validationMessage?: string;
  settingsJson?: string;
  goToSectionBasedOnAnswer?: boolean;
  options: FormFieldOption[];
  mediaUrl?: string;
  inlineImageUrl?: string;
}

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  order: number;
  afterSectionAction?: FormNavigationAction;
  afterSectionTargetId?: string | null;
}

export type FormThemeFontFamily = 'Roboto' | 'Inter' | 'Lato';

export interface FormThemeTextSizes {
  title: number;
  section: number;
  question: number;
  body: number;
}

export interface FormTheme {
  primaryColor: string;
  backgroundColor: string;
  fontFamily: FormThemeFontFamily;
  headerImageKey?: string;
  headerImageUrl?: string;
  textSizes: FormThemeTextSizes;
}

export const DEFAULT_FORM_THEME: FormTheme = {
  primaryColor: '#2563eb',
  backgroundColor: '#f9fafb',
  fontFamily: 'Roboto',
  textSizes: {
    title: 24,
    section: 20,
    question: 16,
    body: 14,
  },
};

export const FORM_THEME_FONT_OPTIONS: FormThemeFontFamily[] = [
  'Roboto',
  'Inter',
  'Lato',
];

export const FORM_THEME_PRIMARY_PRESETS: string[] = [
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#dc2626',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#0d9488',
  '#0891b2',
  '#4f46e5',
  '#9333ea',
  '#64748b',
];

export const FORM_THEME_BACKGROUND_PRESETS: string[] = [
  '#ffffff',
  '#f9fafb',
  '#f3f4f6',
  '#eff6ff',
  '#f5f3ff',
  '#fdf2f8',
];

export function createDefaultFormTheme(primaryColor?: string): FormTheme {
  const color =
    primaryColor && /^#[0-9A-Fa-f]{6}$/.test(primaryColor)
      ? primaryColor.toLowerCase()
      : DEFAULT_FORM_THEME.primaryColor;

  return {
    ...DEFAULT_FORM_THEME,
    primaryColor: color,
    textSizes: { ...DEFAULT_FORM_THEME.textSizes },
  };
}

export function resolveFormTheme(theme?: FormTheme | null): FormTheme {
  if (!theme)
  {
    return createDefaultFormTheme();
  }

  const fontFamily = FORM_THEME_FONT_OPTIONS.includes(
    theme.fontFamily as FormThemeFontFamily
  )
    ? (theme.fontFamily as FormThemeFontFamily)
    : DEFAULT_FORM_THEME.fontFamily;

  return {
    primaryColor:
      theme.primaryColor && /^#[0-9A-Fa-f]{6}$/.test(theme.primaryColor)
        ? theme.primaryColor.toLowerCase()
        : DEFAULT_FORM_THEME.primaryColor,
    backgroundColor:
      theme.backgroundColor && /^#[0-9A-Fa-f]{6}$/.test(theme.backgroundColor)
        ? theme.backgroundColor.toLowerCase()
        : DEFAULT_FORM_THEME.backgroundColor,
    fontFamily,
    headerImageKey: theme.headerImageKey || undefined,
    headerImageUrl: theme.headerImageUrl || undefined,
    textSizes: {
      title: theme.textSizes?.title ?? DEFAULT_FORM_THEME.textSizes.title,
      section: theme.textSizes?.section ?? DEFAULT_FORM_THEME.textSizes.section,
      question: theme.textSizes?.question ?? DEFAULT_FORM_THEME.textSizes.question,
      body: theme.textSizes?.body ?? DEFAULT_FORM_THEME.textSizes.body,
    },
  };
}

export interface Form {
  id: string;
  title: string;
  description?: string;
  publicId: string;
  status: FormStatus;
  accessType: FormAccessType;
  purpose: FormPurpose;
  acceptingResponses: boolean;
  collectEmail: FormCollectEmail;
  limitOneResponsePerUser: boolean;
  showProgressBar: boolean;
  confirmationMessage?: string;
  showSubmitAnotherResponseLink: boolean;
  allowResponseEditing: boolean;
  responseEditingDeadlineDays?: number | null;
  allowDraftResponses: boolean;
  sendResponseCopyToRespondent: boolean;
  fieldsRequiredByDefault: boolean;
  theme?: FormTheme;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  sections: FormSection[];
  fields: FormField[];
}

export interface FormSummary {
  id: string;
  title: string;
  status: FormStatus;
  accessType: FormAccessType;
  purpose: FormPurpose;
  acceptingResponses: boolean;
  responseCount: number;
  updatedAt: string;
  publicId: string;
}

export interface FormDefaultSettings {
  customerId: string;
  collectEmailByDefault: FormCollectEmail;
  fieldsRequiredByDefault: boolean;
  showProgressBarByDefault: boolean;
  allowResponseEditingByDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FormResponseAnswer {
  fieldId: string;
  valueText?: string;
  documentContentId?: string;
  documentId?: string;
  fileName?: string;
  contentType?: string;
  fileBase64?: string;
}

export interface FormResponse {
  id: string;
  formId: string;
  submittedAt: string;
  respondentUserId?: string;
  respondentEmail?: string;
  confirmationMessage?: string;
  showSubmitAnotherResponseLink?: boolean;
  allowResponseEditing?: boolean;
  answers: FormResponseAnswer[];
}

export interface FormResponseDraft {
  id: string;
  formId: string;
  respondentEmail?: string;
  draftData: string;
  updatedAt: string;
  expiresAt: string;
}

export interface CreateFormRequest {
  title: string;
  description?: string;
  purpose?: FormPurpose;
}

export interface UpdateFormRequest {
  title: string;
  description?: string;
  status?: FormStatus;
  accessType?: FormAccessType;
  purpose?: FormPurpose;
  acceptingResponses?: boolean;
  collectEmail?: FormCollectEmail;
  limitOneResponsePerUser?: boolean;
  showProgressBar?: boolean;
  confirmationMessage?: string | null;
  showSubmitAnotherResponseLink?: boolean;
  allowResponseEditing?: boolean;
  responseEditingDeadlineDays?: number | null;
  clearResponseEditingDeadlineDays?: boolean;
  allowDraftResponses?: boolean;
  sendResponseCopyToRespondent?: boolean;
  fieldsRequiredByDefault?: boolean;
  theme?: FormTheme;
  sections?: FormSection[];
  fields?: FormField[];
}

export interface UpdateFormDefaultSettingsRequest {
  collectEmailByDefault: FormCollectEmail;
  fieldsRequiredByDefault: boolean;
  showProgressBarByDefault: boolean;
  allowResponseEditingByDefault: boolean;
}

export interface SubmitFormResponseRequest {
  respondentEmail?: string;
  respondentSessionId?: string;
  answers: FormResponseAnswer[];
}

export interface SaveFormResponseDraftRequest {
  respondentSessionId?: string;
  respondentEmail?: string;
  draftData: string;
}

export interface ExportFormResponsesResult {
  documentId: string;
  fileName: string;
}

export const FORM_FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  ShortText: 'Resposta curta',
  Paragraph: 'Parágrafo',
  MultipleChoice: 'Múltipla escolha',
  Checkboxes: 'Caixas de seleção',
  Dropdown: 'Lista suspensa',
  Date: 'Data',
  Time: 'Horário',
  FileUpload: 'Upload de arquivo',
  Rating: 'Classificação',
  Image: 'Imagem',
  Video: 'Vídeo',
};

export const FORM_STATUS_LABELS: Record<FormStatus, string> = {
  Draft: 'Rascunho',
  Published: 'Publicado',
  Closed: 'Encerrado',
};

export const FORM_ACCESS_TYPE_LABELS: Record<FormAccessType, string> = {
  Public: 'Público',
  Private: 'Privado',
};

export const FORM_PURPOSE_LABELS: Record<FormPurpose, string> = {
  General: 'Geral',
  Support: 'Atendimento',
};

export const FORM_PURPOSE_OPTIONS: {
  value: FormPurpose;
  description: string;
}[] = [
  {
    value: 'General',
    description: 'Pesquisas, coleta de dados e formulários do sistema em geral.',
  },
  {
    value: 'Support',
    description:
      'Formulários do chat/atendimento, com vínculo de campos ao contato.',
  },
];

export const FORM_COLLECT_EMAIL_LABELS: Record<FormCollectEmail, string> = {
  NotCollect: 'Não coletar',
  Optional: 'Opcional',
  Required: 'Obrigatório',
};

export const DEFAULT_CONFIRMATION_MESSAGE =
  'Sua resposta foi registrada. Obrigado!';

export const FIELD_TYPES_WITH_OPTIONS: FormFieldType[] = [
  'MultipleChoice',
  'Checkboxes',
  'Dropdown',
];

export const FIELD_TYPES_WITH_BRANCHING: FormFieldType[] = [
  'MultipleChoice',
  'Dropdown',
];

export const FIELD_TYPES_DISPLAY_ONLY: FormFieldType[] = ['Image', 'Video'];

export function isDisplayOnlyFieldType(type: FormFieldType): boolean {
  return FIELD_TYPES_DISPLAY_ONLY.includes(type);
}
